import { Controller } from '@hotwired/stimulus';

const getColumnWidths = (table) => {
	if (!table) return [];
	const headers = table.querySelectorAll('thead th');
	// index 0 is the sticky red column; we scroll by the remaining columns
	return Array.from(headers)
		.slice(1)
		.map((th) => th.getBoundingClientRect().width)
		.filter((w) => Number.isFinite(w) && w > 0);
};

const getBoundaries = (widths) => {
	let acc = 0;
	return widths.map((w) => {
		acc += w;
		return acc;
	});
};

const findNextBoundary = (boundaries, x) => boundaries.find((b) => b > x + 1);
const findPrevBoundary = (boundaries, x) => {
	for (let i = boundaries.length - 1; i >= 0; i--) {
		if (boundaries[i] < x - 1) return boundaries[i];
	}
	return 0;
};

const waitForScroll = (el, targetLeft, timeoutMs = 600) => {
	return new Promise((resolve) => {
		const start = performance.now();
		const tick = () => {
			if (!el) return resolve();
			const done = Math.abs(el.scrollLeft - targetLeft) <= 2;
			const timedOut = performance.now() - start > timeoutMs;
			if (done || timedOut) return resolve();
			requestAnimationFrame(tick);
		};
		requestAnimationFrame(tick);
	});
};

export default class extends Controller {
	static targets = ['scroller', 'prev', 'next', 'status'];

	connect() {
		this.onScroll = this.onScroll.bind(this);
		this.scrollerTarget.addEventListener('scroll', this.onScroll, { passive: true });
		// wait a tick for layout to settle
		requestAnimationFrame(() => this.updateControls());
	}

	disconnect() {
		this.scrollerTarget?.removeEventListener?.('scroll', this.onScroll);
	}

	onScroll() {
		this.updateControls();
	}

	getScrollState() {
		const scroller = this.scrollerTarget;
		const max = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
		const left = scroller.scrollLeft;
		const atStart = left <= 1;
		const atEnd = left >= max - 1;
		return { left, max, atStart, atEnd };
	}

	getBoundaries() {
		const scroller = this.scrollerTarget;
		const table = scroller.querySelector('table');
		const widths = getColumnWidths(table);
		return getBoundaries(widths);
	}

	getCurrentIndex(boundaries, left) {
		// boundaries are cumulative widths of non-sticky columns
		const idx = boundaries.findIndex((b) => b > left + 1);
		return idx === -1 ? boundaries.length : idx; // 0..n
	}

	updateControls() {
		const { atStart, atEnd } = this.getScrollState();

		if (this.hasPrevTarget) this.prevTarget.setAttribute('aria-disabled', atStart ? 'true' : 'false');
		if (this.hasNextTarget) this.nextTarget.setAttribute('aria-disabled', atEnd ? 'true' : 'false');

		this.updateTabStops();
	}

	getStickyWidth() {
		const table = this.scrollerTarget?.querySelector?.('table');
		const sticky = table?.querySelector?.('thead th:first-child');
		const w = sticky?.getBoundingClientRect?.().width;
		return Number.isFinite(w) ? w : 0;
	}

	getFocusableElements() {
		const root = this.scrollerTarget;
		if (!root) return [];
		return Array.from(
			root.querySelectorAll(
				'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])',
			),
		).filter((el) => !el.hasAttribute('disabled') && el.getAttribute('aria-disabled') !== 'true');
	}

	isElementVisibleHorizontally(el, stickyWidth) {
		const scroller = this.scrollerTarget;
		if (!scroller || !el?.getBoundingClientRect) return true;

		const scrollerRect = scroller.getBoundingClientRect();
		const elRect = el.getBoundingClientRect();
		const leftBound = scrollerRect.left + stickyWidth;
		const rightBound = scrollerRect.right;

		// treat elements in the sticky column as always reachable
		const cell = el.closest?.('th,td');
		if (cell) {
			const isStickyFirstCol =
				cell.classList?.contains?.('compare-firstcol') || cell.cellIndex === 0;
			if (isStickyFirstCol) return true;
		}

		return elRect.right > leftBound + 1 && elRect.left < rightBound - 1;
	}

	updateTabStops() {
		const stickyWidth = this.getStickyWidth();
		const focusables = this.getFocusableElements();

		focusables.forEach((el) => {
			const isVisible = this.isElementVisibleHorizontally(el, stickyWidth);

			if (!isVisible) {
				// store previous tabindex state once
				if (!el.hasAttribute('data-compare-scroll-prev-tabindex')) {
					const prev = el.getAttribute('tabindex');
					el.setAttribute('data-compare-scroll-prev-tabindex', prev == null ? '' : prev);
				}
				el.setAttribute('tabindex', '-1');
			} else if (el.hasAttribute('data-compare-scroll-prev-tabindex')) {
				const prev = el.getAttribute('data-compare-scroll-prev-tabindex');
				el.removeAttribute('data-compare-scroll-prev-tabindex');
				if (prev === '') el.removeAttribute('tabindex');
				else el.setAttribute('tabindex', prev);
			}
		});
	}

	announce(message) {
		if (!this.hasStatusTarget) return;
		// force SR to re-announce same text
		this.statusTarget.textContent = '';
		requestAnimationFrame(() => {
			this.statusTarget.textContent = message;
		});
	}

	async scrollNext() {
		const scroller = this.scrollerTarget;
		if (this.hasNextTarget && this.nextTarget.getAttribute('aria-disabled') === 'true') {
			this.announce('Jste na konci tabulky porovnání.');
			return;
		}
		const boundaries = this.getBoundaries();

		const next = findNextBoundary(boundaries, scroller.scrollLeft);
		if (next == null) {
			this.updateControls();
			this.announce('Jste na konci tabulky porovnání.');
			return;
		}

		scroller.scrollTo({ left: next, behavior: 'smooth' });
		await waitForScroll(scroller, next);
		this.updateControls();

		const state = this.getScrollState();
		if (state.atEnd) {
			this.announce(`Zobrazen sloupec ${boundaries.length} z ${boundaries.length}.`);
			return;
		}

		const idx = this.getCurrentIndex(boundaries, scroller.scrollLeft);
		this.announce(`Zobrazen sloupec ${idx + 1} z ${boundaries.length}.`);
	}

	async scrollPrev() {
		const scroller = this.scrollerTarget;
		if (this.hasPrevTarget && this.prevTarget.getAttribute('aria-disabled') === 'true') {
			this.announce('Jste na začátku tabulky porovnání.');
			return;
		}
		const boundaries = this.getBoundaries();

		const prev = findPrevBoundary(boundaries, scroller.scrollLeft);
		scroller.scrollTo({ left: prev, behavior: 'smooth' });
		await waitForScroll(scroller, prev);
		this.updateControls();

		const state = this.getScrollState();
		if (state.atStart) {
			this.announce(`Zobrazen sloupec 1 z ${boundaries.length}.`);
			return;
		}

		const idx = this.getCurrentIndex(boundaries, scroller.scrollLeft);
		this.announce(`Zobrazen sloupec ${idx + 1} z ${boundaries.length}.`);
	}
}

